import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import {
  Shield, LayoutDashboard, Calendar, Tag, AlertTriangle, BarChart3,
  FileText, Ticket, Bell, Users, Wallet, Calculator, MessageSquare
} from 'lucide-react';
import { isStaffRole, formatRoleName } from './roleUtils';

const moduleIcons = {
  dashboard: LayoutDashboard,
  providers: Users,
  customers: Users,
  bookings: Calendar,
  withdrawals: Wallet,
  categories: Tag,
  disputes: AlertTriangle,
  reports: BarChart3,
  cms: FileText,
  promo_codes: Ticket,
  notifications: Bell,
  accounting: Calculator,
  support_chat: MessageSquare,
};

export default function UserFormModal({
  open,
  onOpenChange,
  editingUser,
  userForm,
  setUserForm,
  managerModules,
  isSaving,
  onSave,
  onCancel
}) {
  const togglePermission = (moduleId, permType) => {
    setUserForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [moduleId]: {
          ...prev.permissions[moduleId],
          [permType]: !prev.permissions[moduleId]?.[permType]
        }
      }
    }));
  };

  const toggleAllPermissions = (moduleId, enable) => {
    setUserForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [moduleId]: { can_view: enable, can_add: enable, can_edit: enable, can_delete: enable }
      }
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${isStaffRole(userForm.role) ? 'sm:max-w-3xl' : 'sm:max-w-xl'} max-h-[90vh] flex flex-col`}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2 space-y-4 mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))}
                className="h-11 mt-1.5"
                placeholder="Juan Dela Cruz"
                data-testid="user-name-input"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                className="h-11 mt-1.5"
                placeholder="juan@example.com"
                data-testid="user-email-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Phone</Label>
              <Input
                value={userForm.phone}
                onChange={(e) => setUserForm(prev => ({ ...prev, phone: e.target.value }))}
                className="h-11 mt-1.5"
                placeholder="+63 912 345 6789"
                data-testid="user-phone-input"
              />
            </div>
            <div>
              <Label>{editingUser ? 'New Password (blank = keep)' : 'Password *'}</Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                className="h-11 mt-1.5"
                placeholder="••••••••"
                data-testid="user-password-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Role *</Label>
              <select
                value={userForm.role}
                onChange={(e) => {
                  const newRole = e.target.value;
                  setUserForm(prev => {
                    const updated = { ...prev, role: newRole };
                    if (isStaffRole(newRole) && !isStaffRole(prev.role)) {
                      const defaultPerms = {};
                      managerModules.forEach(mod => {
                        defaultPerms[mod.id] = { can_view: false, can_add: false, can_edit: false, can_delete: false };
                      });
                      updated.permissions = defaultPerms;
                    }
                    return updated;
                  });
                }}
                className="w-full h-11 mt-1.5 border border-stone-200 rounded-lg px-3"
                data-testid="user-role-select"
                disabled={editingUser && isStaffRole(editingUser.role)}
              >
                <option value="customer">Customer</option>
                <option value="provider">Provider</option>
                <option value="manager">Manager</option>
                <option value="customer_service">Customer Service</option>
                <option value="accountant">Accountant</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl mt-auto">
              <Switch
                checked={userForm.is_active}
                onCheckedChange={(checked) => setUserForm(prev => ({ ...prev, is_active: checked }))}
                data-testid="user-active-toggle"
              />
              <div>
                <p className="font-medium text-slate-700 text-sm">Active Account</p>
              </div>
            </div>
          </div>

          {isStaffRole(userForm.role) && (
            <div className="border-t pt-4 mt-2">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label className="text-base font-semibold">Module Permissions</Label>
                  <p className="text-sm text-slate-500">Configure what this {formatRoleName(userForm.role).toLowerCase()} can access</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {managerModules.map(mod => {
                  const Icon = moduleIcons[mod.id] || Shield;
                  const perms = userForm.permissions[mod.id] || {};
                  const hasAny = perms.can_view || perms.can_add || perms.can_edit || perms.can_delete;
                  const hasAll = perms.can_view && perms.can_add && perms.can_edit && perms.can_delete;

                  return (
                    <div
                      key={mod.id}
                      className={`p-2.5 rounded-lg border transition-colors ${hasAny ? 'border-indigo-300 bg-indigo-50/50' : 'border-slate-200'}`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${hasAny ? 'text-indigo-600' : 'text-slate-400'}`} />
                          <span className="font-medium text-slate-900 text-sm">{mod.name}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleAllPermissions(mod.id, !hasAll)}
                          className={`text-xs px-1.5 py-0.5 rounded ${hasAll ? 'bg-indigo-500 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
                        >
                          {hasAll ? '✓ All' : 'All'}
                        </button>
                      </div>

                      <div className="flex gap-1">
                        {[
                          { key: 'can_view', label: 'V', title: 'View' },
                          { key: 'can_add', label: 'A', title: 'Add' },
                          { key: 'can_edit', label: 'E', title: 'Edit' },
                          { key: 'can_delete', label: 'D', title: 'Delete' }
                        ].map(perm => (
                          <button
                            key={perm.key}
                            type="button"
                            title={perm.title}
                            onClick={() => togglePermission(mod.id, perm.key)}
                            className={`w-7 h-7 rounded text-xs font-bold transition-all ${
                              perms[perm.key]
                                ? 'bg-indigo-500 text-white'
                                : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                            }`}
                            data-testid={`perm-${mod.id}-${perm.key}`}
                          >
                            {perm.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 flex gap-3 pt-4 mt-4 border-t">
          <Button variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={isSaving}
            className="flex-1 bg-slate-900 hover:bg-slate-800"
            data-testid="save-user-btn"
          >
            {isSaving ? 'Saving...' : editingUser ? 'Update User' : 'Create User'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
